import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import {
  BookOpen,
  Search,
  ArrowRight,
  Landmark,
  MapPin,
  Scale,
  Building,
  Flag,
  Users,
  Globe,
  Shield,
  Palette,
  FileText
} from "lucide-react";

const categoryIcons = {
  histoire: Landmark,
  geographie: MapPin,
  valeurs: Scale,
  institutions: Building,
  symboles: Flag,
  culture: Palette,
  europe: Globe,
  droits: Shield,
  societe: Users,
  demarches: FileText
};

const categoryNames = {
  histoire: "Histoire de France",
  geographie: "Géographie",
  valeurs: "Valeurs républicaines",
  institutions: "Institutions",
  symboles: "Symboles nationaux",
  culture: "Culture et patrimoine",
  europe: "Union Européenne",
  droits: "Droits et devoirs",
  societe: "Vie en société",
  demarches: "Démarches administratives"
};

const Fiches = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [fiches, setFiches] = useState([]);
  const [filteredFiches, setFilteredFiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  useEffect(() => {
    const fetchFiches = async () => {
      try {
        const response = await axios.get(`${API}/fiches`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setFiches(response.data);
        setFilteredFiches(response.data);
      } catch (error) {
        toast.error("Erreur lors du chargement des fiches");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchFiches();
  }, [token]);

  useEffect(() => {
    let filtered = fiches;
    
    if (selectedCategory !== "all") {
      filtered = filtered.filter(f => f.category === selectedCategory);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(f => 
        f.title.toLowerCase().includes(term) ||
        f.content.toLowerCase().includes(term)
      );
    }
    
    setFilteredFiches(filtered);
  }, [searchTerm, selectedCategory, fiches]);

  const categories = ["all", ...new Set(fiches.map(f => f.category))];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fiches-page">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 mb-2">
            Fiches de révision
          </h1>
          <p className="text-slate-600">
            Apprenez les notions clés pour réussir votre test civique.
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Rechercher une fiche..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {categories.map((cat) => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat)}
                className={selectedCategory === cat ? "bg-blue-900" : ""}
                data-testid={`filter-${cat}`}
              >
                {cat === "all" ? "Toutes" : categoryNames[cat] || cat}
              </Button>
            ))}
          </div>
        </div>

        {/* Fiches Grid */}
        {filteredFiches.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredFiches.map((fiche) => {
              const Icon = categoryIcons[fiche.category] || BookOpen;
              return (
                <Card 
                  key={fiche.id}
                  className="fiche-card group"
                  onClick={() => navigate(`/fiche/${fiche.id}`)}
                  data-testid={`fiche-${fiche.id}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <span className={`category-badge ${fiche.category}`}>
                      <Icon className="h-3 w-3" />
                      {categoryNames[fiche.category] || fiche.category}
                    </span>
                    <ArrowRight className="h-5 w-5 text-slate-300 group-hover:text-blue-900 group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="text-lg font-serif font-semibold text-slate-900 mb-3 group-hover:text-blue-900 transition-colors">
                    {fiche.title}
                  </h3>
                  <ul className="space-y-1">
                    {fiche.key_points.slice(0, 3).map((point, idx) => (
                      <li key={idx} className="text-sm text-slate-500 flex items-center gap-2">
                        <span className="h-1.5 w-1.5 bg-blue-900 rounded-full"></span>
                        {point}
                      </li>
                    ))}
                  </ul>
                </Card>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Aucune fiche trouvée</p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Fiches;
